import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageDepressionWontGoAway = makeArticle({
  slug: "depression-wont-go-away-even-with-medication",
  title: "Depression Won't Go Away Even With Medication | Neuro Wellness",
  description:
    "If your depression won't go away even with medication, learn about treatment-resistant depression and options like TMS, SAINT®, and SPRAVATO®.",
  canonical: "/learn/depression-wont-go-away-even-with-medication",
  h1: "My Depression Won't Go Away Even With Medication",
  breadcrumbLabel: "Depression Won't Go Away",
  shortAnswer:
    "If your depression won't go away even with medication, you may have treatment-resistant depression — depression that hasn't responded adequately to at least two antidepressant trials. This is common, affecting roughly one-third of people treated for major depressive disorder. Options beyond medication include TMS, SAINT® accelerated TMS, and SPRAVATO® (esketamine), which work through different mechanisms than oral antidepressants. A psychiatric evaluation can confirm whether past trials were adequate and what to try next. If you're having thoughts of self-harm, call or text 988 now.",
  keyTakeaways: [
    "Depression that won't go away on medication may be treatment-resistant.",
    "TRD affects ~1/3 of people treated for depression.",
    "TMS, SAINT®, and SPRAVATO® use different mechanisms.",
    "A psychiatric evaluation can confirm if past trials were adequate.",
    "If having thoughts of self-harm, call or text 988.",
  ],
  sections: [
    {
      heading: "When Medication Isn't Enough",
      paragraphs: [
        "If your depression won't go away even with medication, you're not alone. Roughly one-third of people treated for major depressive disorder don't reach remission after two treatment steps. This is called treatment-resistant depression — depression that hasn't responded adequately to at least two antidepressant trials of adequate dose and duration.",
        "Treatment-resistant depression doesn't mean your depression can't be treated. It means standard antidepressants haven't worked adequately, and other options may be appropriate.",
      ],
    },
    {
      heading: "Why Medication May Not Be Working",
      paragraphs: [
        "Several factors can cause persistent depression despite medication:",
      ],
      list: [
        "Inadequate dose or duration — the trial wasn't long enough or the dose too low",
        "Wrong medication — this specific drug isn't the right match",
        "Misdiagnosis — bipolar depression mistaken for unipolar depression",
        "Comorbid conditions — anxiety, PTSD, substance use, medical issues",
        "Treatment-resistant depression — genuine non-response to standard treatments",
      ],
    },
    {
      heading: "Options Beyond Medication",
      paragraphs: [
        "TMS, SAINT®, and SPRAVATO® work through different mechanisms than oral antidepressants. TMS and SAINT® use magnetic brain stimulation. SPRAVATO® works through NMDA receptor modulation. These treatments are FDA-cleared or approved specifically for depression that hasn't responded to medication. They may help when standard antidepressants haven't.",
      ],
    },
    {
      heading: "What to Do Next",
      paragraphs: [
        "Reach out for a psychiatric evaluation. Bring your medication history. A comprehensive review can confirm whether past trials were adequate, screen for underlying conditions, and identify what to try next. You don't have to keep living with depression that won't go away.",
      ],
    },
    {
      heading: "If You're Having Thoughts of Self-Harm",
      paragraphs: [
        "If you're having thoughts of harming yourself, please call or text 988 (Suicide & Crisis Lifeline) now, or go to your nearest emergency room. You can also chat at 988lifeline.org. Help is available right now.",
      ],
    },
  ],
  faqs: [
    {
      question:
        "My depression won't go away even with medication — what can I do?",
      answer:
        "You may have treatment-resistant depression. Options include TMS, SAINT®, and SPRAVATO®, which work through different mechanisms than antidepressants. A psychiatric evaluation can confirm if past trials were adequate and what to try next. If having thoughts of self-harm, call or text 988.",
    },
    {
      question: "Why isn't my medication working for my depression?",
      answer:
        "Several reasons: inadequate dose or duration, wrong medication, misdiagnosis, comorbid conditions, or treatment-resistant depression. A psychiatric evaluation can identify the cause and recommend next steps.",
    },
    {
      question: "What if my depression won't go away?",
      answer:
        "If depression persists despite medication, treatments like TMS, SAINT®, and SPRAVATO® work through different mechanisms and may help. A psychiatric evaluation can review your history and identify what to try next. You're not out of options.",
    },
    {
      question: "Can TMS help if medication isn't working?",
      answer:
        "Yes. TMS is FDA-cleared specifically for adults whose depression hasn't improved on antidepressants. It uses magnetic brain stimulation, not medication, and doesn't cause systemic side effects. A psychiatric evaluation can determine if TMS is appropriate.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
